import { muiLicense } from '@diligentcorp/atlas-mui-license';
import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { CssBaseline } from '@mui/material';
import { getI18n } from 'react-i18next';
import { Localization } from '@mui/material/locale';
import { AtlasThemeProvider } from '@diligentcorp/atlas-theme-mui';
import { lensThemeOptions } from '@diligentcorp/atlas-theme-mui/lib/themes/lens';
import { atlasThemeExtensions } from './theme/atlasThemeExtensions';

import queryClient from './queryClient';
import OidcProvider from './components/OidcProvider';
import LoadingSpinner from './components/LoadingSpinner';
import { useUser } from './components/OidcProvider/OidcProvider';
import { muiLocales } from './locales/muiLocales';
import { FALLBACK_LNG } from './locales';
import { determineUserLocale } from './helpers/determineUserLocale';
import { AppRoutes } from './routes/AppRoutes';
import { UserProfileProvider, useUserProfile } from './contexts/UserProfileContext';
import { ToastProvider } from '../hooks/useToast';
import { ScrollToTop } from './components/ScrollToTop';
import { SuspendedOrganization } from './components/SuspendedOrganization';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';

// Org-wide MUI X Pro license — must run before any MUI X Pro component mounts.
muiLicense.apply();

function AppContent() {
  const { userProfile, isLoading } = useUser();
  const { isOrganizationSuspended, isLoading: isProfileLoading } = useUserProfile();
  const [locale, setLocale] = React.useState<Localization[]>(muiLocales[FALLBACK_LNG]);

  function updateHtmlLocale(locale: string) {
    const I18n = getI18n();
    I18n.changeLanguage(locale).then((_resolve) => (document.documentElement.lang = locale));
  }

  React.useEffect(() => {
    // Determine locale using priority cascade:
    // 1. User profile locale
    // 2. Organization locale
    // 3. Browser locale
    // 4. Fallback to en-US
    const supportedLocales = Object.keys(muiLocales);
    const selectedLocale = determineUserLocale(userProfile, supportedLocales);

    if (muiLocales[selectedLocale] !== locale) {
      setLocale(muiLocales[selectedLocale]);
      updateHtmlLocale(selectedLocale);
      
      // Update document title with translated app name
      const I18n = getI18n();
      document.title = I18n.t('app_name');
    }
  }, [userProfile, locale]);

  // Log profile details to console when userProfile changes
  React.useEffect(() => {
    if (userProfile) {
      console.log('User Profile:', userProfile);
    }
  }, [userProfile]);

  // Show loading spinner while OIDC or profile is loading
  if (isLoading || isProfileLoading) {
    return <LoadingSpinner />;
  }

  // Check if organization is suspended - block all access if true
  if (isOrganizationSuspended) {
    return (
      <AtlasThemeProvider
        locales={locale}
        themeOptions={lensThemeOptions}
        themeExtensions={atlasThemeExtensions}
      >
        <CssBaseline />
        <SuspendedOrganization />
      </AtlasThemeProvider>
    );
  }

  return (
    <AtlasThemeProvider
      locales={locale}
      themeOptions={lensThemeOptions}
      themeExtensions={atlasThemeExtensions}
    >
      <CssBaseline />
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </AtlasThemeProvider>
  );
}

function AppShell() {
  return (
    <QueryClientProvider client={queryClient}>
      <OidcProvider>
        <UserProfileProvider>
          <ScrollToTop />
          <AppContent />
        </UserProfileProvider>
      </OidcProvider>
    </QueryClientProvider>
  );
}

const router = createBrowserRouter([
  {
    path: '*',
    element: <AppShell />,
    errorElement: <RouteErrorBoundary />,
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}

